import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/App";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Users, Euro, Package, ShoppingCart,
  Clock, AlertTriangle, Sparkles, Search, ChevronRight, BarChart3,
  Loader2, RefreshCw,
} from "lucide-react";

const fmtEur = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);
const fmtNum = (n) => new Intl.NumberFormat("it-IT").format(Number(n) || 0);
const fmtPct = (n) => (n === null || n === undefined ? "—" : `${n > 0 ? "+" : ""}${n.toFixed(1)}%`);

function DeltaBadge({ pct }) {
  if (pct === null || pct === undefined) return <Badge variant="outline" className="text-xs text-zinc-500">N/D</Badge>;
  const up = pct >= 0;
  return (
    <Badge variant="outline" className={`text-xs ${up ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-red-500/40 text-red-300 bg-red-500/10"}`}>
      {up ? <TrendingUp className="w-3 h-3 mr-1" /> : <TrendingDown className="w-3 h-3 mr-1" />}
      {fmtPct(pct)}
    </Badge>
  );
}

function StatCard({ icon: Icon, label, value, delta, color = "text-amber-300", testid }) {
  return (
    <Card className="bg-zinc-900/60 border-white/10" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-zinc-400 text-[10px] uppercase tracking-wider">
            <Icon className={`w-3.5 h-3.5 ${color}`} />
            {label}
          </div>
          {delta !== undefined && <DeltaBadge pct={delta} />}
        </div>
        <div className={`text-2xl font-bold ${color}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

export default function AdminClientAnalytics() {
  const { authAxios } = useAuth();
  const [days, setDays] = useState(30);
  // Range custom: quando entrambi valorizzati, il backend usa questi al posto di ``days``.
  // "" ⇒ non attivo (fallback a ``days``).
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const usingCustomRange = Boolean(dateFrom && dateTo);
  const [overview, setOverview] = useState(null);
  const [report, setReport] = useState(null);
  const [reportPeriod, setReportPeriod] = useState("daily");
  const [topClients, setTopClients] = useState([]);
  const [dormants, setDormants] = useState([]);
  const [dormantDays, setDormantDays] = useState(60);
  // Ordinamento lista Dormienti: "giorni" (default, desc), "eur" (desc), "nome" (asc)
  const [dormantSort, setDormantSort] = useState("giorni");
  const [dormantSortDir, setDormantSortDir] = useState("desc");
  const [dropped, setDropped] = useState([]);
  const [trends, setTrends] = useState([]);
  const [frequency, setFrequency] = useState([]);
  const [loading, setLoading] = useState(true);

  // AI summary state
  const [aiText, setAiText] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  // Cross-sell state (per ricerca cliente)
  const [crossSearch, setCrossSearch] = useState("");
  const [crossResults, setCrossResults] = useState([]);
  const [crossClientPick, setCrossClientPick] = useState(null);
  const [crossSuggestions, setCrossSuggestions] = useState(null);
  const [crossLoading, setCrossLoading] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const rangeQS = usingCustomRange
        ? `&date_from=${dateFrom}&date_to=${dateTo}`
        : "";
      const [ovr, rep, top, dorm, drop, tr, freq] = await Promise.all([
        authAxios.get(`/admin/client-analytics/overview?days=${days}${rangeQS}`),
        authAxios.get(`/admin/client-analytics/daily-report?period=${reportPeriod}`),
        authAxios.get(`/admin/client-analytics/top-clients?days=${days}&limit=20${rangeQS}`),
        authAxios.get(`/admin/client-analytics/dormant-clients?dormant_days=${dormantDays}`),
        authAxios.get(`/admin/client-analytics/dropped-items?lookback_days=180&dropout_days=45&min_orders=3&limit=100`),
        authAxios.get(`/admin/client-analytics/category-trends?days=${days}${rangeQS}`),
        authAxios.get(`/admin/client-analytics/order-frequency?days=180&limit=50`),
      ]);
      setOverview(ovr.data);
      setReport(rep.data);
      setTopClients(top.data.items || []);
      setDormants(dorm.data.items || []);
      setDropped(drop.data.items || []);
      setTrends(tr.data.items || []);
      setFrequency(freq.data.items || []);
    } catch (e) {
      toast.error("Errore caricamento analisi");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authAxios, days, reportPeriod, dormantDays, usingCustomRange, dateFrom, dateTo]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Lista dormienti ordinata secondo la scelta utente. Client-side: la
  // lista è già in memoria (max ~500 record), evita round-trip al backend.
  const sortedDormants = useMemo(() => {
    const arr = [...dormants];
    const dir = dormantSortDir === "asc" ? 1 : -1;
    const key = {
      giorni: (c) => c.giorni_da_ultimo ?? 0,
      eur: (c) => c.eur_storico ?? 0,
      nome: (c) => (c.ragione_sociale || "").toLowerCase(),
    }[dormantSort] || ((c) => c.giorni_da_ultimo ?? 0);
    arr.sort((a, b) => {
      const va = key(a); const vb = key(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    return arr;
  }, [dormants, dormantSort, dormantSortDir]);

  const handleAISummary = async () => {
    setAiLoading(true);
    setAiText("");
    try {
      const r = await authAxios.post(`/admin/client-analytics/ai-summary`, { period: reportPeriod });
      setAiText(r.data.summary || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione sintesi AI");
    } finally {
      setAiLoading(false);
    }
  };

  // Client search (per cross-sell)
  useEffect(() => {
    const q = crossSearch.trim();
    if (q.length < 2) {
      setCrossResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const r = await authAxios.get(`/admin/clients-lookup?q=${encodeURIComponent(q)}&limit=8`);
        setCrossResults(r.data.items || []);
      } catch {
        setCrossResults([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [crossSearch, authAxios]);

  const loadCrossSell = async (client) => {
    setCrossClientPick(client);
    setCrossSearch("");
    setCrossResults([]);
    setCrossLoading(true);
    try {
      const r = await authAxios.get(`/admin/client-analytics/cross-sell-suggestions?client_id=${client.id}&days=180&top=15`);
      setCrossSuggestions(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore cross-sell");
      setCrossSuggestions(null);
    } finally {
      setCrossLoading(false);
    }
  };

  const sortedTrends = useMemo(() => [...trends].sort((a, b) => b.eur_cur - a.eur_cur), [trends]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        <PageHeader title="Analisi Clienti" />

        {/* Period filter */}
        <Card className="bg-zinc-900/60 border-white/10 mb-4">
          <CardContent className="p-3 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-zinc-400 uppercase tracking-wider">Periodo:</span>
            <Select
              value={String(days)}
              onValueChange={(v) => setDays(Number(v))}
              disabled={usingCustomRange}
            >
              <SelectTrigger className="w-36 bg-zinc-950 border-white/10 disabled:opacity-50" data-testid="period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Ultimi 7 gg</SelectItem>
                <SelectItem value="30">Ultimi 30 gg</SelectItem>
                <SelectItem value="90">Ultimi 90 gg</SelectItem>
                <SelectItem value="180">Ultimi 180 gg</SelectItem>
                <SelectItem value="365">Ultimi 365 gg</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-zinc-600">oppure</span>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-zinc-500">Da</span>
              <Input
                type="date"
                value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 w-36 bg-zinc-950 border-white/10 text-xs"
                data-testid="period-date-from"
              />
              <span className="text-zinc-500">a</span>
              <Input
                type="date"
                value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 w-36 bg-zinc-950 border-white/10 text-xs"
                data-testid="period-date-to"
              />
              {usingCustomRange && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs text-zinc-400 hover:text-white hover:bg-white/5"
                  onClick={() => { setDateFrom(""); setDateTo(""); }}
                  data-testid="period-range-reset"
                  title="Cancella range custom"
                >
                  ✕
                </Button>
              )}
            </div>
            <Button
              size="icon"
              variant="outline"
              className="border-white/10 ml-auto"
              onClick={loadAll}
              disabled={loading}
              data-testid="reload-btn"
              title="Ricarica"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </CardContent>
        </Card>

        {/* KPI overview */}
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="kpi-grid">
            <StatCard icon={Euro} label="Fatturato" value={fmtEur(overview.fatturato_eur)} delta={overview.fatturato_delta_pct} color="text-amber-300" testid="kpi-fatturato" />
            <StatCard icon={Users} label="Clienti Attivi" value={fmtNum(overview.n_clienti_attivi)} color="text-emerald-300" testid="kpi-clienti" />
            <StatCard icon={Package} label="Articoli Unici" value={fmtNum(overview.n_articoli_unici)} color="text-cyan-300" testid="kpi-articoli" />
            <StatCard icon={ShoppingCart} label="Ordini App" value={fmtNum(overview.n_ordini_app)} color="text-fuchsia-300" testid="kpi-ordini" />
          </div>
        )}

        {/* Report del giorno/settimana */}
        <Card className="bg-zinc-900/60 border-amber-500/20 mb-4" data-testid="report-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-300" />
                <h3 className="text-lg font-semibold text-white">Relazione {reportPeriod === "daily" ? "del giorno" : "della settimana"}</h3>
              </div>
              <div className="flex gap-2">
                <Select value={reportPeriod} onValueChange={setReportPeriod}>
                  <SelectTrigger className="w-32 h-8 bg-zinc-950 border-white/10 text-xs" data-testid="report-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Giornaliera</SelectItem>
                    <SelectItem value="weekly">Settimanale</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  onClick={handleAISummary}
                  disabled={aiLoading}
                  data-testid="ai-summary-btn"
                >
                  {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Sintesi AI
                </Button>
              </div>
            </div>
            {report && (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Fatturato</div>
                  <div className="text-xl font-bold text-amber-300">{fmtEur(report.fatturato_eur)}</div>
                  <DeltaBadge pct={report.fatturato_delta_pct} />
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Clienti</div>
                  <div className="text-xl font-bold text-emerald-300">{report.n_clienti}</div>
                  <div className="text-[10px] text-zinc-500">prev: {report.n_clienti_prev}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Ordini App</div>
                  <div className="text-xl font-bold text-fuchsia-300">{report.n_ordini_app}</div>
                  <div className="text-[10px] text-zinc-500">prev: {report.n_ordini_app_prev}</div>
                </div>
                <div>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider">Dormienti &gt;30gg</div>
                  <div className="text-xl font-bold text-red-300">{report.n_dormienti_30gg}</div>
                </div>
              </div>
            )}
            {report?.top_clienti?.length > 0 && (
              <div className="border-t border-white/5 pt-3 mt-2">
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Top clienti del periodo</div>
                <div className="flex flex-wrap gap-2">
                  {report.top_clienti.map((c) => (
                    <Badge key={c.codice_cli} variant="outline" className="border-amber-500/30 text-amber-200 bg-amber-500/5">
                      {c.ragione_sociale} · {fmtEur(c.eur)}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {aiText && (
              <div className="border-t border-amber-500/20 pt-3 mt-3 bg-amber-500/5 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg" data-testid="ai-summary-text">
                <div className="flex items-center gap-1 text-[10px] text-amber-300 uppercase tracking-wider mb-2">
                  <Sparkles className="w-3 h-3" /> Sintesi AI (Claude)
                </div>
                <p className="text-sm text-zinc-200 leading-relaxed whitespace-pre-line">{aiText}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs principali */}
        <Tabs defaultValue="top" className="mb-6">
          <TabsList className="bg-zinc-900/60 border border-white/10 w-full overflow-x-auto justify-start h-auto flex-wrap">
            <TabsTrigger value="top" data-testid="tab-top">Top Clienti</TabsTrigger>
            <TabsTrigger value="dormienti" data-testid="tab-dormant">Dormienti</TabsTrigger>
            <TabsTrigger value="abbandonati" data-testid="tab-dropped">Articoli Abbandonati</TabsTrigger>
            <TabsTrigger value="crosssell" data-testid="tab-crosssell">Cross-Sell</TabsTrigger>
            <TabsTrigger value="trend" data-testid="tab-trend">Trend Categorie</TabsTrigger>
            <TabsTrigger value="frequenza" data-testid="tab-freq">Frequenza</TabsTrigger>
          </TabsList>

          {/* TOP CLIENTI */}
          <TabsContent value="top">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                {loading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…</div>
                ) : topClients.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">Nessun dato per il periodo.</div>
                ) : (
                  <div className="space-y-1.5">
                    {topClients.map((c, i) => (
                      <div key={c.codice_cli} className="flex items-center justify-between p-2 rounded hover:bg-white/5" data-testid={`top-client-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-zinc-500 w-6">#{i + 1}</span>
                            <span className="font-medium text-white truncate">{c.ragione_sociale}</span>
                            <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{c.codice_cli}</Badge>
                          </div>
                          <div className="text-[11px] text-zinc-500 ml-8">
                            {c.citta} {c.provincia ? `(${c.provincia})` : ""} · Agente {c.cod_agente || "—"} · {c.righe} righe
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <div className="font-bold text-amber-300">{fmtEur(c.eur)}</div>
                          <div className="text-[10px] text-zinc-500">{fmtNum(c.qta)} pz</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DORMIENTI */}
          <TabsContent value="dormienti">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
                  <span className="text-zinc-400">Dormienti da più di:</span>
                  <Select value={String(dormantDays)} onValueChange={(v) => setDormantDays(Number(v))}>
                    <SelectTrigger className="w-24 h-7 bg-zinc-950 border-white/10 text-xs" data-testid="dormant-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 gg</SelectItem>
                      <SelectItem value="60">60 gg</SelectItem>
                      <SelectItem value="90">90 gg</SelectItem>
                      <SelectItem value="180">180 gg</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-zinc-600">·</span>
                  <span className="text-zinc-400">Ordina per:</span>
                  <Select value={dormantSort} onValueChange={setDormantSort}>
                    <SelectTrigger className="w-32 h-7 bg-zinc-950 border-white/10 text-xs" data-testid="dormant-sort-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="giorni">Giorni</SelectItem>
                      <SelectItem value="eur">Fatturato</SelectItem>
                      <SelectItem value="nome">Nome cliente</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-zinc-300 hover:text-white hover:bg-white/5"
                    onClick={() => setDormantSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    data-testid="dormant-sort-dir-btn"
                    title={dormantSortDir === "desc" ? "Ordine decrescente (clicca per invertire)" : "Ordine crescente (clicca per invertire)"}
                  >
                    {dormantSortDir === "desc" ? "↓ Desc" : "↑ Asc"}
                  </Button>
                  <span className="text-zinc-600 ml-auto">{sortedDormants.length} risultati</span>
                </div>
                {loading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…</div>
                ) : sortedDormants.length === 0 ? (
                  <div className="text-center py-8 text-emerald-400">Nessun cliente dormiente — ottimo!</div>
                ) : (
                  <div className="space-y-1.5">
                    {sortedDormants.slice(0, 100).map((c, i) => (
                      <div key={c.codice_cli} className="flex items-center justify-between p-2 rounded hover:bg-white/5" data-testid={`dormant-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <Clock className="w-3.5 h-3.5 text-red-400 shrink-0" />
                            <span className="font-medium text-white truncate">{c.ragione_sociale}</span>
                            <Badge variant="outline" className="text-[10px] text-red-300 border-red-500/30 bg-red-500/5">
                              {c.giorni_da_ultimo} gg
                            </Badge>
                          </div>
                          <div className="text-[11px] text-zinc-500 ml-5">
                            {c.citta} · Agente {c.cod_agente || "—"} · ultimo {c.ultimo_acquisto || "—"}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <div className="text-xs text-zinc-400">{fmtEur(c.eur_storico)}</div>
                          <div className="text-[10px] text-zinc-500">storico</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ARTICOLI ABBANDONATI */}
          <TabsContent value="abbandonati">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                <p className="text-xs text-zinc-400 mb-2">
                  Articoli che il cliente comprava ≥ 3 volte negli ultimi 180gg, ma non più da almeno 45 giorni. Da riproporre.
                </p>
                {loading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…</div>
                ) : dropped.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">Nessun articolo abbandonato — tutti i clienti continuano a comprare i loro abituali.</div>
                ) : (
                  <div className="space-y-1.5">
                    {dropped.map((d, i) => (
                      <div key={`${d.codice_cli}-${d.sku}`} className="flex items-center justify-between p-2 rounded hover:bg-white/5 gap-2" data-testid={`dropped-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-orange-400 shrink-0" />
                            <span className="font-medium text-white truncate">{d.ragione_sociale}</span>
                          </div>
                          <div className="text-[11px] text-zinc-400 ml-5 truncate">
                            <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10 mr-1">{d.sku}</Badge>
                            {d.descr}
                          </div>
                          <div className="text-[10px] text-zinc-500 ml-5">
                            {d.n_acquisti} acquisti · ultimo {d.ultimo_acquisto} ({d.giorni_da_ultimo} gg fa)
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs text-amber-300">{fmtEur(d.eur_storico)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CROSS-SELL */}
          <TabsContent value="crosssell">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                <p className="text-xs text-zinc-400 mb-2">
                  Scegli un cliente per vedere quali articoli i clienti simili (stesso agente/provincia) comprano frequentemente, ma lui no.
                </p>
                <div className="relative mb-3">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <Input
                    placeholder="Cerca cliente per nome o codice…"
                    value={crossSearch}
                    onChange={(e) => setCrossSearch(e.target.value)}
                    className="pl-9 bg-zinc-950 border-white/10"
                    data-testid="crosssell-search"
                  />
                  {crossResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-zinc-900 border border-white/10 rounded-md shadow-lg max-h-64 overflow-auto">
                      {crossResults.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className="w-full text-left p-2 hover:bg-white/5 text-sm border-b border-white/5 last:border-0"
                          onClick={() => loadCrossSell(c)}
                          data-testid={`crosssell-pick-${c.codice_cli}`}
                        >
                          <div className="text-white truncate">{c.ragione_sociale}</div>
                          <div className="text-[11px] text-zinc-500">{c.codice_cli} · {c.citta}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {crossClientPick && (
                  <div className="mb-3 p-2 rounded bg-amber-500/10 border border-amber-500/30">
                    <div className="text-xs text-amber-200">
                      Suggerimenti per: <b>{crossClientPick.ragione_sociale}</b>
                    </div>
                  </div>
                )}
                {crossLoading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Cercando articoli…</div>
                ) : crossSuggestions ? (
                  crossSuggestions.items?.length > 0 ? (
                    <div className="space-y-1.5">
                      <div className="text-[10px] text-zinc-500 mb-2">
                        Confrontato con {crossSuggestions.n_clienti_simili_totale} clienti ({crossSuggestions.criterio_simili}).
                      </div>
                      {crossSuggestions.items.map((it, i) => (
                        <div key={it.sku} className="flex items-center justify-between p-2 rounded hover:bg-white/5" data-testid={`crosssell-item-${i}`}>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                              <Badge variant="outline" className="text-[10px] text-zinc-400 border-white/10">{it.sku}</Badge>
                              <span className="text-sm text-white truncate">{it.descr}</span>
                            </div>
                          </div>
                          <div className="text-right shrink-0 ml-2">
                            <div className="text-xs text-emerald-300">{it.n_clienti_simili} clienti</div>
                            <div className="text-[10px] text-zinc-500">{fmtEur(it.eur_tot)} storico</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-zinc-500">{crossSuggestions.info || "Nessun suggerimento — il cliente già compra tutto."}</div>
                  )
                ) : !crossClientPick ? (
                  <div className="text-center py-8 text-zinc-500">Cerca un cliente per iniziare.</div>
                ) : null}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TREND CATEGORIE */}
          <TabsContent value="trend">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                {loading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…</div>
                ) : sortedTrends.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">Nessun dato.</div>
                ) : (
                  <div className="space-y-2">
                    {sortedTrends.map((t, i) => {
                      const maxEur = sortedTrends[0]?.eur_cur || 1;
                      const widthPct = Math.max(2, (t.eur_cur / maxEur) * 100);
                      return (
                        <div key={t.macro_category} data-testid={`trend-${i}`}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-white">{t.macro_category}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-amber-300">{fmtEur(t.eur_cur)}</span>
                              <DeltaBadge pct={t.delta_pct} />
                            </div>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-amber-500 to-amber-300" style={{ width: `${widthPct}%` }} />
                          </div>
                          <div className="text-[10px] text-zinc-500 mt-0.5">prev: {fmtEur(t.eur_prev)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FREQUENZA */}
          <TabsContent value="frequenza">
            <Card className="bg-zinc-900/60 border-white/10">
              <CardContent className="p-3">
                <p className="text-xs text-zinc-400 mb-2">
                  Quanti giorni in media tra un acquisto e l&apos;altro per ogni cliente (ultimi 180gg).
                </p>
                {loading ? (
                  <div className="text-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Caricamento…</div>
                ) : frequency.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500">Nessun dato.</div>
                ) : (
                  <div className="space-y-1.5">
                    {frequency.map((c, i) => (
                      <div key={c.codice_cli} className="flex items-center justify-between p-2 rounded hover:bg-white/5" data-testid={`freq-${i}`}>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-mono text-zinc-500 w-6">#{i + 1}</span>
                            <span className="font-medium text-white truncate">{c.ragione_sociale}</span>
                          </div>
                          <div className="text-[11px] text-zinc-500 ml-8">
                            {c.n_acquisti_distinti} acquisti distinti · Agente {c.cod_agente || "—"}
                          </div>
                        </div>
                        <div className="text-right shrink-0 ml-2">
                          <div className="font-bold text-emerald-300">{c.frequenza_giorni} gg</div>
                          <div className="text-[10px] text-zinc-500">{fmtEur(c.eur_periodo)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
